import { motion } from "framer-motion";
import {
  X,
  Check,
  FileText,
  MessageSquareOff,
  TrendingDown,
  Users2,
  BookOpen,
  Rocket,
} from "lucide-react";

const typical = [
  { icon: FileText, text: "Brief" },
  { icon: MessageSquareOff, text: "Post" },
  { icon: TrendingDown, text: "Done" },
];

const affixWay = [
  {
    icon: Users2,
    title: "Creator collaboration",
    note: "Co-created concepts, not paid placements.",
  },
  {
    icon: BookOpen,
    title: "Narrative building",
    note: "Story arcs designed for memory, not impressions.",
  },
  {
    icon: Rocket,
    title: "Structured rollout",
    note: "Sequenced drops engineered for cultural lift.",
  },
];

const affixListVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.13, delayChildren: 0.35 },
  },
};

const affixItemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Differentiation() {
  return (
    <section
      id="differentiation"
      data-testid="differentiation-section"
      className="relative pt-8 md:pt-10 pb-16 md:pb-20 px-6 md:px-10 surface-2"
    >
      <div className="mx-auto max-w-7xl">
        {/* Heading — eyebrow small, headline dominant */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span
            data-testid="diff-eyebrow"
            className="inline-block text-[11px] font-bold tracking-[0.28em] uppercase text-[#2563EB]"
          >
            The Difference
          </span>
          <h2
            data-testid="differentiation-headline"
            className="mt-3 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-slate-900 dark:text-white"
          >
            We Don't Do Influencer Marketing{" "}
            <span className="font-serif-italic font-medium gradient-text">
              the Usual Way.
            </span>
          </h2>
        </motion.div>

        {/* Cards */}
        <div className="relative mt-12 grid lg:grid-cols-[30fr_70fr] gap-5 lg:gap-6 items-stretch">
          {/* Soft blue glow behind Affix card only */}
          <div
            aria-hidden
            className="hidden lg:block pointer-events-none absolute -z-0 right-[-6%] top-[8%] w-[55%] h-[85%] rounded-[40px] blur-3xl opacity-60"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(37,99,235,0.35) 0%, rgba(59,130,246,0.18) 45%, transparent 75%)",
            }}
          />

          {/* === TYPICAL CAMPAIGN — intentionally dull === */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 0.9, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            data-testid="diff-typical-campaign"
            className="order-2 lg:order-1 relative overflow-hidden rounded-xl p-6 md:p-7 bg-slate-200/60 dark:bg-white/[0.025] border border-slate-300/70 dark:border-white/[0.06]"
            style={{
              boxShadow:
                "inset 0 2px 6px rgba(15,23,42,0.08), inset 0 -1px 2px rgba(15,23,42,0.04)",
            }}
          >
            {/* huge faint watermark word */}
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-2 -right-3 font-heading font-black text-[72px] md:text-[84px] leading-none tracking-tight text-slate-400/[0.07] dark:text-white/[0.04] select-none"
            >
              BASIC
            </span>

            <div className="relative flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-slate-300/70 dark:bg-white/[0.06] flex items-center justify-center text-slate-500/70 dark:text-slate-500">
                <X className="w-3.5 h-3.5" strokeWidth={1.4} />
              </div>
              <span className="text-[10px] tracking-[0.26em] uppercase text-slate-500/80 dark:text-slate-500 font-medium">
                Typical Campaign
              </span>
            </div>

            <h3 className="relative mt-4 font-heading font-medium text-base md:text-lg text-slate-500 dark:text-slate-400 leading-snug">
              Transactional. Forgettable.
            </h3>

            {/* flat checklist — no rounded blocks, just rectangles */}
            <ol className="relative mt-6 divide-y divide-slate-300/60 dark:divide-white/[0.05] border-y border-slate-300/60 dark:border-white/[0.05]">
              {typical.map((t, i) => {
                const I = t.icon;
                return (
                  <li
                    key={t.text}
                    data-testid={`diff-typical-item-${t.text.toLowerCase()}`}
                    className="flex items-center gap-3 px-1 py-2.5"
                  >
                    <span className="text-[10px] font-mono text-slate-500/70 dark:text-slate-500 w-5 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <I
                      className="w-3.5 h-3.5 text-slate-500/70 dark:text-slate-500"
                      strokeWidth={1.4}
                    />
                    <span className="text-[13.5px] text-slate-600/80 dark:text-slate-400 font-normal">
                      {t.text}
                    </span>
                  </li>
                );
              })}
            </ol>

            <p className="relative mt-5 text-[12px] text-slate-500/70 dark:text-slate-500 leading-relaxed">
              One-off posts. No narrative arc. No compounding equity.
            </p>
          </motion.div>

          {/* === AFFIX CAMPAIGN === */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.02 }}
            data-testid="diff-affix-campaign"
            className="order-1 lg:order-2 group relative overflow-hidden rounded-2xl p-8 md:p-10 text-white transition-shadow duration-500 hover:shadow-[0_40px_100px_-30px_rgba(37,99,235,0.7)]"
            style={{
              background:
                "linear-gradient(135deg, #0B1C3D 0%, #1E3A8A 35%, #2563EB 75%, #3B82F6 110%)",
              boxShadow:
                "0 25px 70px -25px rgba(37, 99, 235, 0.55), 0 10px 25px -12px rgba(15, 23, 42, 0.4)",
            }}
          >
            {/* Inner lighting / glow orbs */}
            <div
              aria-hidden
              className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-blue-300/30 blur-3xl transition-all duration-700 group-hover:bg-blue-300/50 group-hover:scale-110"
            />
            <div
              aria-hidden
              className="absolute -bottom-28 -left-16 w-64 h-64 rounded-full bg-indigo-400/25 blur-3xl transition-all duration-700 group-hover:bg-indigo-400/40"
            />

            {/* Top inner-light highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay"
              style={{
                background:
                  "radial-gradient(ellipse at 25% 0%, rgba(255,255,255,0.55) 0%, transparent 50%)",
              }}
            />

            {/* Subtle grid */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
                maskImage:
                  "radial-gradient(ellipse at top right, black 30%, transparent 75%)",
                WebkitMaskImage:
                  "radial-gradient(ellipse at top right, black 30%, transparent 75%)",
              }}
            />

            <div className="relative flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-md ring-1 ring-white/25 flex items-center justify-center text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]">
                <Check className="w-4 h-4" />
              </div>
              <span className="text-[10.5px] tracking-[0.26em] uppercase text-blue-100 font-semibold">
                Affix Campaign
              </span>
            </div>

            <motion.h3
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: 0.35 }}
              className="relative mt-5 font-heading font-bold text-xl md:text-2xl lg:text-[26px] leading-[1.2] tracking-tight"
            >
              Structured. Compounding. Memorable.
            </motion.h3>

            {/* Structured blocks with vertical flow line connector */}
            <motion.ol
              variants={affixListVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="relative mt-7 space-y-2.5"
            >
              {/* vertical flow line */}
              <span
                aria-hidden
                className="absolute left-[22px] top-7 bottom-7 w-px bg-gradient-to-b from-transparent via-white/35 to-transparent"
              />

              {affixWay.map((t) => {
                const I = t.icon;
                return (
                  <motion.li
                    key={t.title}
                    variants={affixItemVariants}
                    data-testid={`diff-affix-item-${t.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="relative flex items-start gap-3.5 rounded-xl bg-white/[0.08] border border-white/15 backdrop-blur-md px-4 py-3 transition-all duration-300 group-hover:bg-white/[0.13] group-hover:border-white/25 group-hover:translate-x-0.5"
                    style={{
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.12), 0 6px 18px -12px rgba(0,0,0,0.5)",
                    }}
                  >
                    <div className="relative shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-white/30 to-white/10 ring-1 ring-white/30 flex items-center justify-center text-white">
                      <I className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[14.5px] font-semibold text-white leading-tight">
                        {t.title}
                      </p>
                      <p className="mt-0.5 text-[12px] text-blue-100/80 leading-snug">
                        {t.note}
                      </p>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ol>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: 0.95 }}
              className="relative mt-6 text-[12.5px] text-blue-100/85 leading-relaxed"
            >
              Campaigns built like products — engineered to keep paying rent in
              audience memory.
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
